/**
 * Tenant access panel — surfaces current `vm_access` state and lets
 * the user redeem an invite code to unlock VM endpoints.
 */
import { type Component, Show, createResource, createSignal } from "solid-js";

import { fetchAccess, redeemInvite } from "@/api/client";
import type { TenantAccess } from "@/api/generated";

const AccessPanel: Component = () => {
  const [access, { refetch }] = createResource<TenantAccess>(fetchAccess);
  const [code, setCode] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);
  const [ok, setOk] = createSignal<string | null>(null);

  const onRedeem = async (e: Event) => {
    e.preventDefault();
    if (busy()) return;
    const trimmed = code().trim();
    if (!trimmed) {
      setErr("Enter an invite code");
      return;
    }
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      const r = await redeemInvite(trimmed);
      setOk(`vm_access granted: max_vms=${r.max_vms}, max_gpus=${r.max_gpus}`);
      setCode("");
      void refetch();
    } catch (e) {
      // ApiRequestError from apiFetch (duck-typed so any Error works).
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: unknown }).message)
          : "redeem failed";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card">
      <header>
        <h2>VM access</h2>
        <p class="muted">
          VM endpoints (<code>/api/v1/vms</code>) are deny-by-default. Redeem an
          invite code or ask an operator to grant access to your tenant.
        </p>
      </header>

      <Show when={access()} fallback={<p class="muted">Loading access…</p>}>
        {(a) => (
          <dl class="kv">
            <dt>Tenant</dt>
            <dd>
              <code>{a().tenant_id}</code>
            </dd>
            <dt>vm_access</dt>
            <dd>{a().vm_access ? "granted" : "not granted"}</dd>
            <dt>max VMs</dt>
            <dd>{a().max_vms}</dd>
            <dt>max GPUs</dt>
            <dd>{a().max_gpus}</dd>
            <Show when={a().granted_by}>
              <dt>granted by</dt>
              <dd>
                <code>{a().granted_by}</code>
              </dd>
            </Show>
          </dl>
        )}
      </Show>

      <form class="row" onSubmit={onRedeem}>
        <label class="grow">
          <span>Invite code</span>
          <input
            type="text"
            placeholder="z01-xxxxxxxxxxxxxxxxxxxxxx"
            value={code()}
            onInput={(e) => setCode(e.currentTarget.value)}
            disabled={busy()}
          />
        </label>
        <button type="submit" disabled={busy()}>
          {busy() ? "Redeeming…" : "Redeem"}
        </button>
      </form>

      <Show when={err()}>
        <p class="error">{err()}</p>
      </Show>
      <Show when={ok()}>
        <p class="ok">{ok()}</p>
      </Show>
    </section>
  );
};

export default AccessPanel;
